import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton, Icon, Text } from '../../../../../components';
import { COLORS } from '../../../../../theme/tokens';
import { CopyDisabled } from '../../../components/FieldRow/FieldRow.styles';
import {
  Block,
  Row,
  RowIcon,
  Mono,
  MaskedDots,
  EyeToggle,
  ActionLink,
  Divider,
  OnceNote,
} from './CredentialBlock.styles';

export type CredentialBlockProps = {
  /** The full `local@domain`. */
  address: string;
  /** This mailbox's plaintext password, present only while it is held this session. */
  secret: string | undefined;
  /** When the password was last set (ISO); absent when it never was. Drives only the link's verb. */
  passwordSetAt: string | undefined;
  /** Issue the first password (nothing to invalidate, so no confirmation). */
  onGenerate: () => void;
  /** Reset the password (a new one replaces the old; the old stops working). */
  onReset: () => void;
  /** True while a reset's simulated round trip is in flight: the link disables and says so, so a
   *  double click cannot issue two passwords with the second silently winning. */
  resetting: boolean;
  /** Nothing may be issued (a deleted mailbox, a failed domain): Generate / Change password is disabled. */
  disabled?: boolean;
};

/**
 * Address and password, the two things an operator copies into a mail client, as Figma 56:2670
 * draws them: two 26px rows on one inset card with a rule between them, each led by a 16px glyph in
 * a 24px column so the two values start at the same x. Each copy button FOLLOWS its value, a normal
 * gap after it: the copy belongs to the value it copies, and that proximity beats a shared right
 * edge (which the rows no longer have). The address row: mail glyph, the address, copy, and nothing
 * at its right edge. The password row: the EYE as its leading icon, dots (or the plaintext once
 * revealed), copy, then Change password alone at the far right.
 *
 * The row draws the same in every state; what the controls DO depends on whether a plaintext is
 * held this session (a mailbox created or reset in this visit):
 *
 *  - held: the eye reveals it (masked by default, back to masked when a new one arrives) and copy
 *    copies it.
 *  - not held: the eye and copy are DISABLED, each with a title saying the password is not stored
 *    and Change password sets a new one. Nothing is ever read back: the provider does not hand a
 *    password out and this build stores none, so a live control here would reveal nothing.
 *
 * SETTLED, and the reason the disabled branch is permanent: the frames draw a reveal control on a
 * page the operator has returned to, which would need the password read back, and it never can be.
 * The service returns a password once, in the response that set it, and stores it nowhere (not
 * hashed, not at all); the provider answers a read with asterisks. Rotation is the only way forward,
 * and it signs every mail client out (Change password confirms that first). Do not ship a control that
 * does nothing.
 *
 * The eye is local state on this component, never a reducer dispatch, so toggling it re-renders
 * nothing else on the page.
 */
export const CredentialBlock = ({
  address,
  secret,
  passwordSetAt,
  onGenerate,
  onReset,
  resetting,
  disabled = false,
}: CredentialBlockProps): React.ReactElement => {
  const { t } = useTranslation();
  // Hidden by default, and back to hidden whenever a NEW plaintext arrives (a reset while the
  // previous one was revealed must not show the new one unasked).
  const [revealed, setRevealed] = React.useState(false);
  React.useEffect(() => {
    setRevealed(false);
  }, [secret]);
  const onToggle = React.useCallback(() => setRevealed((was) => !was), []);
  const held = secret !== undefined && secret !== '';
  const notStored = t('The password is not stored. Change it to set a new one.');
  // Generate the first one (nothing to invalidate) or change it (the old one stops working); the
  // gate lives in the handler as well as on the button.
  // The never-set branch (Generate) issues a generated password straight away, with no dialog, so it
  // offers no typed password; Change password opens the dialog with the editable field.
  const firstTime = !passwordSetAt && !held;
  const onLinkClick = React.useCallback(() => {
    if (resetting || disabled) return;
    if (firstTime) onGenerate();
    else onReset();
  }, [resetting, disabled, firstTime, onGenerate, onReset]);

  return (
    <Block>
      {/* The rows lead with an icon, not a label, so each is named for assistive tech via
          role="group" + aria-label; the icon alone would leave the row unnamed. */}
      <Row role="group" aria-label={t('Address')}>
        <RowIcon>
          <Icon name="mail" size={16} color={COLORS.iconBright} />
        </RowIcon>
        <Mono title={address}>{address}</Mono>
        <CopyButton value={address} label={t('Copy address')} />
      </Row>

      {/* Only ever between the two rows: not above the address, not below the password. */}
      <Divider role="separator" />

      <Row role="group" aria-label={t('Password')}>
        <RowIcon>
          <EyeToggle
            type="button"
            aria-label={revealed ? t('Hide password') : t('Show password')}
            aria-pressed={held ? revealed : undefined}
            disabled={!held}
            title={held ? undefined : notStored}
            onClick={onToggle}
          >
            <Icon name={revealed ? 'eye' : 'eye-off'} size={16} />
          </EyeToggle>
        </RowIcon>
        {held && revealed ? (
          <Mono data-testid="mailbox-password-plain">{secret}</Mono>
        ) : (
          <MaskedDots aria-hidden="true" data-testid="mailbox-password-masked">
            ••••••••
          </MaskedDots>
        )}
        {/* Copy right after the value it copies, then Change password alone at the far right. It
            opens the dialog where the operator chooses the new password (typed or generated). */}
        {held ? (
          <CopyButton value={secret} label={t('Copy password')} />
        ) : (
          <CopyDisabled type="button" disabled aria-label={t('Copy password')} title={notStored}>
            <Icon name="copy" size={14} />
          </CopyDisabled>
        )}
        {/* The explicit name matters for Generate: its visible label is short, its name complete. */}
        <ActionLink
          type="button"
          onClick={onLinkClick}
          disabled={resetting || disabled}
          aria-busy={resetting}
          aria-label={resetting ? t('Changing…') : firstTime ? t('Generate password') : t('Change password')}
        >
          {resetting ? (
            <Text text="Changing…" tag="span" />
          ) : firstTime ? (
            <Text text="Generate" tag="span" />
          ) : (
            <Text text="Change password" tag="span" />
          )}
        </ActionLink>
      </Row>
      {held ? (
        // Shown ONCE and stored nowhere: said plainly, while there is still something to save.
        <div data-testid="mailbox-password-once">
          <OnceNote text="Shown once. Save it now: you need it to sign in to webmail or a mail client." tag="span" />
        </div>
      ) : null}
    </Block>
  );
};

export default CredentialBlock;
